import { AppEnum, AppEnumHelper, DocumentEnums, DOMAIN_TYPES, MandateEnums, OptionHelper, RecordMap } from "@/Shared";
import { DocumentFormValues } from "../DocumentFormValidation";
import { DocumentModel } from "../DocumentModel";
import { LeaseModel } from "@/Domains/Lease";
import { PropertyModel } from "@/Domains/Property";
import { MandateModel } from "@/Domains/Mandate";

/**
 * =====================================================
 * Class: DocumentFormViewModel
 * =====================================================
 *
 * Description:
 * Encapsulates the form state and logic for Document creation and editing.
 * Provides access to enumerations, resource options, and computed values
 * to populate form fields in the UI.
 *
 * Responsibilities:
 * - Store current document data for form binding
 * - Compute category/type options dynamically
 * - Provide resource-specific options (lease, property, mandate)
 * - Track creation vs. edit mode
 * - Provide helper methods for category/type changes
 *
 * Notes:
 * - Designed to work with DocumentFormValues for form libraries
 * - Can be extended with additional computed properties for UI needs
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
export class DocumentFormViewModel {
	public id: number;
	public resourceId!: number;
	public documentTitle: string;
	public documentType!: string;
	public documentCategory!: string;
	public documentSource: string | null;
	public documentTypeOptions: AppEnum[] = [];

	private filteredLeaseMapByType!: RecordMap<LeaseModel>;

	/**
	 * Constructor
	 *
	 * @param viewEnums - Application enums for document categories/types
	 * @param mandateEnums - Mandate-specific enumerations
	 * @param leaseMap - List of all leases for selection
	 * @param propertyMap - List of all properties for selection
	 * @param mandateMap - List of all mandates for selection
	 * @param _document - Optional existing document for edit mode
	 */
	constructor(
		private viewEnums: DocumentEnums,
		private mandateEnums: MandateEnums,
		private leaseMap: RecordMap<LeaseModel>,
		private propertyMap: RecordMap<PropertyModel>,
		private mandateMap: RecordMap<MandateModel>,
		private _document?: DocumentModel,
		private _curCategory? : string,
		private _curType ? : string,
		private _resourceId? : number
	) {
		this.id = _document?.id ?? 0;
		this.documentTitle = _document?.documentTitle ?? "";
		this.documentSource = _document?.documentSource ?? "";
		this.resourceId = _document?.resourceId ?? 0;

		this.documentCategory = this._document?.documentCategory ?? this._curCategory ?? "";
		this.documentType = this._document?.documentType ?? this._curType ?? "";
		this.resourceId = this._document?.resourceId ?? this._resourceId ?? 0;

		this.onCategoryChanged(this.documentCategory);
		this.initLeaseListByType(this.documentType);

	}

	/** =====================================================
	* Initialize Lease List
	* =====================================================
	*
	* Filters lease list according to the currently selected document type.
	*/
	private initLeaseListByType(type?: string) {
		this.filteredLeaseMapByType = this.leaseMap.filter(lease => lease.leaseType === type);
	}

	/** =====================================================
	 * Form Values Getter
	 * =====================================================
	 *
	 * Returns the current document form values compatible with validation library.
	 */
	get formValues(): DocumentFormValues {
		return {
			id: this.id,
			resourceId: this.resourceId,
			documentTitle: this.documentTitle,
			documentType: this.documentType,
			documentCategory: this.documentCategory,
			documentSource: this.documentSource,
			uploadedFile: undefined,

		} as DocumentFormValues;
	}

	/** =====================================================
	 * Source Checks
	 * =====================================================
	 *
	 * Determines if the document category is linked to a known resource type.
	 */
	isSourceKnown() {
		return [
			DOMAIN_TYPES.Mandate,
			DOMAIN_TYPES.Lease,
			DOMAIN_TYPES.Property
		].some(t => t === this.documentCategory);
	}

	/** =====================================================
	* Retrieve Resource Options
	* =====================================================
	*
	* Returns the relevant options depending on the document category.
	*/
	getKnownSourceOptions(): AppEnum[] {
		let options = [] as AppEnum[];
		switch (this.documentCategory) {
			case DOMAIN_TYPES.Mandate:
				options = this.mandateOptions;
				break;
			case DOMAIN_TYPES.Lease:
				options = this.leaseOptions;
				break;
			case DOMAIN_TYPES.Property:
				options = this.propertyOptions;
				break;
			default:
				break;
		}
		return options;
	}

	/** =====================================================
	 * Event Handlers
	 * =====================================================
	 *
	 * Update type options when category changes.
	 */
	onCategoryChanged(newCategory?: string) {

		this.documentTypeOptions = (
			AppEnumHelper.getByValue(this.viewEnums.DocumentCategoryEnum, newCategory)
			?? this.viewEnums.DocumentCategoryEnum[0]
		)?.children
			?? [];
	}

	/** Update filtered lease list when type changes for lease category */
	onTypeChanged(newType: string) {
		if (this.documentCategory === DOMAIN_TYPES.Lease) {
			this.initLeaseListByType(newType);
		}
	}

	/** =====================================================
	 * Option Getters
	 * =====================================================
	 */
	get categoryOptions(): AppEnum[] {
		return this.viewEnums.DocumentCategoryEnum;
	}
	get typeOptions(): AppEnum[] {
		return this.documentTypeOptions;
	}
	get leaseOptions(): AppEnum[] {
		return OptionHelper.leaseOptions(this.filteredLeaseMapByType.toArray());
	}
	get propertyOptions(): AppEnum[] {
		return OptionHelper.propertyOptions(this.propertyMap.toArray(), this.mandateEnums.PropertyTypeEnum);
	}
	get mandateOptions(): AppEnum[] { return OptionHelper.mandateOptions(this.mandateMap.toArray(), this.mandateEnums); }

	/** =====================================================
	 * Labels & Titles
	 * =====================================================
	 */
	get title(): string {
		return this._document
			? `Document N° ${this.id} - ${this.documentType}`
			: "Création de Document";
	}

	isCreationMode(): boolean {
		return !this.id;
	}

	get documentTypeLabel(): string {
		return AppEnumHelper.getLabel(this.viewEnums.DocumentCategoryEnum, this.documentCategory);
	}
	get documentCategoryLabel(): string {
		return AppEnumHelper.getLabel(this.viewEnums.DocumentTypeEnum, this.documentType);
	}
}
